<template>
  <div>
    <button
      v-if="showRequestButton"
      @click="openModal"
      class="flex justify-center py-2 px-2 md:px-4 border border-white shadow-sm text-sm font-medium text-white rounded-full hover:bg-primary-default/50 items-center mr-2"
    >
      <span class="ml-1">Gegevens patiënt opvragen</span>
    </button>

    <modal-dialog :modal-open-status="showModal" :show-close-button="false">
      <template #header>
        <h2 class="text-lg font-bold text-primary-default">Gegevens patiënt opvragen</h2>
      </template>
      <template #body>
        <div class="grid grid-rows gap-4 text-sm text-gray-600">
          <p>
            Stuur de patiënt een email met het verzoek om toestemming te geven voor gegevensdeling op het
            Voorschrijfportaal.
          </p>
          <p>
            De patiënt ontvangt een email waarna hij of zij zelf kan aangeven van welke afdeling de gegevens gedeeld
            mogen worden binnen het voorschrijfportaal.
          </p>

          <Alert
            v-if="!hasPatientEmail"
            type="alert"
            :html="true"
            html-message="Voor deze patiënt is nog geen emailadres bekend. Vul hieronder het emailadres van de patiënt in."
          />

          <div v-if="!hasPatientEmail">
            <label for="patientDataSharingRequestEmail" class="block text-sm font-medium text-gray-700 mb-1">
              Emailadres patiënt <span class="text-red-600">*</span>
            </label>
            <input
              id="patientDataSharingRequestEmail"
              v-model="emailInput"
              type="text"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-default focus:outline-none focus:ring-1 focus:ring-primary-default"
              placeholder="bijv. naam@email.nl"
              autocomplete="email"
            />
            <p v-if="sameAsUserEmailError" class="text-red-600 text-sm mt-1">
              Het emailadres mag niet hetzelfde zijn als uw eigen emailadres.
            </p>
          </div>

          <p v-if="submitError" class="text-red-600 text-sm">{{ submitError }}</p>
        </div>
      </template>
      <template #footer>
        <div class="flex w-full items-center justify-between gap-4">
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-700 underline"
            :disabled="isSending"
            @click="closeModal"
          >
            Annuleren
          </button>
          <v-button tagelement="button" md primary :disabled="isSending || !canSend" @click="sendEmail">
            Email versturen
          </v-button>
        </div>
      </template>
    </modal-dialog>
  </div>
</template>

<script>
import { computed, ref, watch, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { combineLatest } from 'rxjs';
import router from '@/router';
import axios from 'axios';
import Alert from '@/components/webcomponents/Alert';
import { activeCustomerId$, activeDepartment$ } from '@/services';

export default {
  name: 'PatientDataSharingRequest',
  components: {
    Alert
  },
  props: {
    patientGegevens: {
      type: Object,
      default: () => ({})
    },
    user: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const instance = getCurrentInstance();
    const $emitter = instance?.appContext.config.globalProperties.$emitter;
    const showModal = ref(false);
    const emailInput = ref('');
    const isSending = ref(false);
    const submitError = ref('');
    const sameAsUserEmailError = ref(false);
    const isSharingEnabled = ref(null);
    const isSharingStatusLoading = ref(false);
    const activeCustomerId = ref(null);
    const activeDepartment = ref(null);
    let sharingStatusSubscription = null;

    const showPatientDetails = computed(() => {
      if (instance?.appContext.config.globalProperties.$isInIframe) {
        return true;
      }

      if (router.currentRoute.value.path.includes('/aanvullenmagazijn')) {
        return false;
      }

      return router.currentRoute.value.meta.hidePatientDetails !== true;
    });

    const showStatus = computed(() => router.currentRoute.value.name === 'patientdashboard');

    const loadSharingStatus = async () => {
      if (showModal.value) {
        return;
      }

      if (!activeCustomerId.value || !activeDepartment.value) {
        isSharingEnabled.value = null;
        isSharingStatusLoading.value = false;
        return;
      }

      isSharingStatusLoading.value = true;

      try {
        const response = await axios.get('Customer/GetCustomerDataSharingStatus');
        const data = response.data || {};
        const sharingEnabled = data.IsSharingEnabled ?? data.isSharingEnabled;

        isSharingEnabled.value = typeof sharingEnabled === 'boolean' ? sharingEnabled : null;
      } catch (error) {
        console.error('Error loading customer data sharing status', error);
        isSharingEnabled.value = null;
      } finally {
        isSharingStatusLoading.value = false;
      }
    };

    const showRequestButton = computed(() => {
      if (!showPatientDetails.value || !showStatus.value || !activeCustomerId.value || !activeDepartment.value) {
        return false;
      }

      if (isSharingStatusLoading.value) {
        return false;
      }

      return isSharingEnabled.value === false;
    });

    const patientEmail = computed(() => {
      return props.patientGegevens?.EmailAdres || props.patientGegevens?.Emailadres || '';
    });

    const hasPatientEmail = computed(() => {
      return patientEmail.value !== null && patientEmail.value !== '';
    });

    const isValidEmailFormat = (email) => {
      return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    };

    const normalizedUserEmail = computed(() => (props.user?.email || props.user?.Email || '').trim().toLowerCase());

    const isSameAsUserEmail = (email) => {
      if (!email || !normalizedUserEmail.value) {
        return false;
      }
      return email.trim().toLowerCase() === normalizedUserEmail.value;
    };

    const validateEmail = (email, updateErrors = true) => {
      if (updateErrors) {
        sameAsUserEmailError.value = false;
        submitError.value = '';
      }

      if (!hasPatientEmail.value) {
        if (!email || !email.trim()) {
          return false;
        }
        if (!isValidEmailFormat(email.trim())) {
          return false;
        }
        if (isSameAsUserEmail(email)) {
          if (updateErrors) {
            sameAsUserEmailError.value = true;
          }
          return false;
        }
      }

      return true;
    };

    const canSend = computed(() => {
      if (hasPatientEmail.value) {
        return true;
      }
      return validateEmail(emailInput.value, false);
    });

    watch(emailInput, (value) => {
      if (showModal.value && !hasPatientEmail.value) {
        validateEmail(value);
      }
    });

    const openModal = () => {
      submitError.value = '';
      sameAsUserEmailError.value = false;
      emailInput.value = '';
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      submitError.value = '';
      sameAsUserEmailError.value = false;
      emailInput.value = '';
      loadSharingStatus();
    };

    const sendEmail = async () => {
      const emailToUse = hasPatientEmail.value ? patientEmail.value : emailInput.value?.trim();

      if (!validateEmail(emailToUse)) {
        return;
      }

      isSending.value = true;
      submitError.value = '';

      try {
        const payload = hasPatientEmail.value ? {} : { Email: emailToUse };
        await axios.post('Customer/RequestCustomerDataSharing', payload);
        closeModal();
      } catch (error) {
        submitError.value =
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          'Het versturen van de email is niet gelukt. Probeer het later opnieuw.';
      } finally {
        isSending.value = false;
      }
    };

    const onPatientDataSharingUpdated = (enable) => {
      isSharingEnabled.value = !!enable;
      isSharingStatusLoading.value = false;
    };

    onMounted(() => {
      sharingStatusSubscription = combineLatest([activeCustomerId$, activeDepartment$]).subscribe(async ([customerId, department]) => {
        activeCustomerId.value = customerId;
        activeDepartment.value = department;
        isSharingEnabled.value = null;
        await loadSharingStatus();
      });

      $emitter?.on('patientDataSharingUpdated', onPatientDataSharingUpdated);
    });

    onBeforeUnmount(() => {
      if (sharingStatusSubscription) {
        sharingStatusSubscription.unsubscribe();
      }

      $emitter?.off('patientDataSharingUpdated', onPatientDataSharingUpdated);
    });

    return {
      showModal,
      emailInput,
      isSending,
      submitError,
      sameAsUserEmailError,
      showRequestButton,
      hasPatientEmail,
      canSend,
      openModal,
      closeModal,
      sendEmail
    };
  }
};
</script>
